import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, ShoppingCart, AlertCircle, Plus, Trash2, Search, Check, X } from 'lucide-react';
import { purchaseService } from '../services/purchaseService';
import { paymentService } from '../services/paymentService';
import { supplierService } from '../services/supplierService';
import { productService } from '../services/productService';
import { PurchaseRequestDTO, PurchaseResponseDTO, PurchaseFormData, PurchaseItemFormData } from '../types/purchase';
import { FornecedorResponseDTO } from '../types/supplier';
import { ProductResponseDTO } from '../types/product';
import { alertService } from '../services/alertService';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { formatFromRaw, getNextRawOnKey, parseRawToNumber, rawFromNumber } from '../utils/decimalMask';
// Padronizado via alertService (SweetAlert2)

const PurchaseInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [suppliers, setSuppliers] = useState<FornecedorResponseDTO[]>([]);
  const [products, setProducts] = useState<ProductResponseDTO[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [editLocked, setEditLocked] = useState(false);
  
  // Estados para controlar loading das pesquisas
  const [searchingSuppliers, setSearchingSuppliers] = useState(false);
  const [searchingProducts, setSearchingProducts] = useState(false);
  
  const [formData, setFormData] = useState<PurchaseFormData>({
    fornecedorId: '',
    dataCompra: new Date().toISOString().split('T')[0],
    dataEntrega: '',
    numeroNotaFiscal: '',
    valorFrete: '0',
    valorDesconto: '0',
    observacoes: '',
    status: 'PENDENTE',
    itens: []
  });

  // Máscara decimal para quantidade (3 casas)
  const QUANTIDADE_DECIMALS = 3;
  const [quantidadeRawMap, setQuantidadeRawMap] = useState<Record<number, string>>({});
  // Máscara decimal para preço (2 casas)
  const PRECO_DECIMALS = 2;
  const [precoRawMap, setPrecoRawMap] = useState<Record<number, string>>({});

  useEffect(() => {
    loadInitialData();
    if (isEditing) {
      loadPurchase();
    }
  }, [id, isEditing]);

  useEffect(() => {
    if (isEditing && editLocked && id) {
      navigate(`/compras/${id}`);
    }
  }, [isEditing, editLocked, id]);

  const loadInitialData = async () => {
    try {
      console.log('Carregando dados iniciais...');
      const [suppliersResponse, productsResponse] = await Promise.all([
        supplierService.list({ size: 1000 }),
        productService.list({ size: 1000, ativo: true })
      ]);

      console.log('Resposta dos fornecedores:', suppliersResponse);
      console.log('Resposta dos produtos:', productsResponse);

      if (suppliersResponse.success && suppliersResponse.data) {
        setSuppliers(suppliersResponse.data.content || []);
      }

      if (productsResponse.success && productsResponse.data) {
        setProducts(productsResponse.data.content || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
      alertService.toast('error', 'Erro ao carregar dados. Tente novamente.');
    }
  };

  const loadPurchase = async (purchaseId?: string) => {
    const targetId = purchaseId || id;
    if (!targetId) return;

    try {
      setLoadingData(true);
      const [response, paymentsRes] = await Promise.all([
        purchaseService.getById(targetId),
        paymentService.listByPurchasePath(targetId)
      ]);
      
      if (response.success && response.data) {
        const purchase = response.data;
        setFormData({
          fornecedorId: purchase.fornecedorId,
          dataCompra: purchase.dataCompra.split('T')[0],
          dataEntrega: purchase.dataEntrega ? purchase.dataEntrega.split('T')[0] : '',
          numeroNotaFiscal: purchase.numeroNotaFiscal || '',
          valorFrete: purchase.valorFrete?.toString() || '0',
          valorDesconto: purchase.valorDesconto?.toString() || '0',
          observacoes: purchase.observacoes || '',
          status: purchase.status,
          itens: purchase.itens.map(item => ({
            id: item.id,
            produtoId: item.produtoId,
            produtoNome: item.produtoNome,
            quantidade: item.quantidade.toString(),
            precoUnitario: item.precoUnitario.toString(),
            valorTotal: item.valorTotal.toString(),
            observacoes: item.observacoes || ''
          }))
        });

        const raw = paymentsRes.data as any;
        const items = Array.isArray(raw) ? raw : (raw?.content || []);
        const hasPayments = (items.length || 0) > 0;
        const locked = purchase.status !== 'PENDENTE' || hasPayments;
        setEditLocked(locked);
      }
    } catch (error) {
      console.error('Erro ao carregar compra:', error);
      alertService.toast('error', 'Erro ao carregar compra.');
      navigate('/compras');
    } finally {
      setLoadingData(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fornecedorId.trim()) {
      newErrors.fornecedorId = 'Fornecedor é obrigatório';
    }

    if (!formData.dataCompra.trim()) {
      newErrors.dataCompra = 'Data da compra é obrigatória';
    }

    if (formData.itens.length === 0) {
      newErrors.itens = 'Pelo menos um item deve ser adicionado';
    }

    // Validar itens
    formData.itens.forEach((item, index) => {
      if (!item.produtoId) {
        newErrors[`item_${index}_produto`] = 'Produto é obrigatório';
      }
      if (!item.quantidade || parseFloat(item.quantidade) <= 0) {
        newErrors[`item_${index}_quantidade`] = 'Quantidade deve ser maior que zero';
      }
      if (!item.precoUnitario || parseFloat(item.precoUnitario) <= 0) {
        newErrors[`item_${index}_preco`] = 'Preço deve ser maior que zero';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      alertService.toast('error', 'Por favor, corrija os erros no formulário.');
      return;
    }

    try {
      setLoading(true);
      
      const purchaseData: PurchaseRequestDTO = {
        fornecedorId: formData.fornecedorId.trim(),
        dataCompra: formData.dataCompra,
        dataEntrega: formData.dataEntrega || undefined,
        numeroNotaFiscal: formData.numeroNotaFiscal.trim() || undefined,
        valorTotal: calculateTotal(),
        valorFrete: parseFloat(formData.valorFrete) || 0,
        valorDesconto: parseFloat(formData.valorDesconto) || 0,
        observacoes: formData.observacoes.trim() || undefined,
        status: formData.status,
        itens: formData.itens.map(item => ({
          produtoId: item.produtoId,
          quantidade: parseFloat(item.quantidade),
          precoUnitario: parseFloat(item.precoUnitario),
          valorTotal: parseFloat(item.valorTotal),
          observacoes: item.observacoes.trim() || undefined
        }))
      };

      let response;
      if (isEditing) {
        response = await purchaseService.update(id!, purchaseData);
      } else {
        response = await purchaseService.create(purchaseData);
      }

      if (response.success) {
        alertService.toast('success', `Compra ${isEditing ? 'atualizada' : 'criada'} com sucesso!`);
        // Redirecionar automaticamente para a tela de Pagamento (V2)
        const purchaseIdForRedirect = (response.data?.id || id)?.toString();
        if (purchaseIdForRedirect) {
          navigate(`/compras/${purchaseIdForRedirect}/pagamento`);
        }
      }
    } catch (error) {
      console.error('Erro ao salvar compra:', error);
      alertService.toast('error', `Erro ao ${isEditing ? 'atualizar' : 'criar'} compra.`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof PurchaseFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleItemChange = (index: number, field: keyof PurchaseItemFormData, value: string) => {
    const newItens = [...formData.itens];
    newItens[index] = { ...newItens[index], [field]: value };
    setFormData(prev => ({ ...prev, itens: newItens }));
    
    // Limpar erros do item
    const errorKey = `item_${index}_${field === 'produtoId' ? 'produto' : field === 'precoUnitario' ? 'preco' : field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      itens: [...prev.itens, {
        produtoId: '',
        produtoNome: '',
        quantidade: '',
        precoUnitario: '',
        valorTotal: '0',
        observacoes: ''
      }]
    }));
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      itens: prev.itens.filter((_, i) => i !== index)
    }));
  };

  const handleCancel = () => {
    navigate('/compras');
  };

  const handleConfirmPurchase = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      await purchaseService.confirm(id);
      alertService.success('Compra confirmada com sucesso!');
      
      // Recarregar os dados da compra para atualizar o status
      await loadPurchase(id);
    } catch (error: any) {
      console.error('Erro ao confirmar compra:', error);
      alertService.error(error.response?.data?.message || 'Erro ao confirmar compra');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPurchase = async () => {
    if (!id) return;
    const result = await alertService.confirmDelete(
      'Cancelar Compra',
      'Tem certeza que deseja cancelar esta compra? Esta ação não pode ser desfeita!',
      'Sim, cancelar',
      'Não, manter'
    );

    if (!result.isConfirmed) return;
    
    try {
      setLoading(true);
      const motivoPrompt = await alertService.input(
        'Motivo do cancelamento',
        'Descreva o motivo do cancelamento'
      );
      if (!motivoPrompt.isConfirmed || !motivoPrompt.value) {
        setLoading(false);
        return;
      }

      await purchaseService.cancel(id, motivoPrompt.value);
      await alertService.success('Compra cancelada!', 'A compra foi cancelada com sucesso.');
      
      // Recarregar os dados da compra para atualizar o status
      await loadPurchase(id);
    } catch (error: any) {
      console.error('Erro ao cancelar compra:', error);
      await alertService.error('Erro!', error.response?.data?.message || 'Erro ao cancelar compra');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    return formData.itens.reduce((total, item) => {
      const quantidade = parseFloat(item.quantidade) || 0;
      const preco = parseFloat(item.precoUnitario) || 0;
      return total + (quantidade * preco);
    }, 0);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getProductName = (produtoId: string) => {
    const product = products.find(p => p.id === produtoId);
    return product ? product.nome : '';
  };

  const getStatusLabel = (status: PurchaseFormData['status']) => {
    switch (status) {
      case 'PENDENTE': return 'Pendente';
      case 'CONFIRMADA': return 'Confirmada';
      case 'EM_TRANSITO': return 'Em Trânsito';
      case 'ENTREGUE': return 'Entregue';
      case 'FINALIZADA': return 'Finalizada';
      case 'CANCELADA': return 'Cancelada';
      case 'PARCIALMENTE_ENTREGUE': return 'Parcialmente Entregue';
      default: return status;
    }
  };

  if (loadingData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3 text-gray-500">
            <ShoppingCart className="w-6 h-6 animate-pulse" />
            <span>Carregando compra...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center space-x-3">
            <ShoppingCart className="h-8 w-8 text-red-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Editar Compra' : 'Nova Compra'}
              </h1>
              <p className="text-gray-600">
                {isEditing ? 'Atualize as informações da compra' : 'Preencha as informações da nova compra'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informações Gerais */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Informações Gerais</h2>
          
          <div className="space-y-6">
            {/* Primeira linha - Fornecedor (largura total) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fornecedor *
              </label>
              <SearchableSelect
                options={suppliers.map(supplier => ({
                  id: supplier.id,
                  nome: supplier.razaoSocial,
                  descricao: supplier.cnpj
                }))}
                value={formData.fornecedorId}
                onChange={(value) => handleInputChange('fornecedorId', String(value))}
                placeholder="Selecione um fornecedor"
                loading={searchingSuppliers}
                error={errors.fornecedorId}
              />
            </div>

            {/* Segunda linha - Data da Compra e Número da Nota Fiscal */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data da Compra *
                </label>
                <input
                  type="date"
                  value={formData.dataCompra}
                  onChange={(e) => handleInputChange('dataCompra', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                    errors.dataCompra ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.dataCompra && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.dataCompra}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número da Nota Fiscal
                </label>
                <input
                  type="text"
                  value={formData.numeroNotaFiscal}
                  onChange={(e) => handleInputChange('numeroNotaFiscal', e.target.value)}
                  placeholder="Digite o número da nota fiscal"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Nova linha - Status e Valor Total (readonly) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <input
                  type="text"
                  value={getStatusLabel(formData.status)}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Valor Total</label>
                <input
                  type="text"
                  value={formatCurrency(calculateTotal())}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                />
              </div>
            </div>

            {/* Terceira linha - Observações (largura total) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações
              </label>
              <textarea
                value={formData.observacoes}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
                placeholder="Observações sobre a compra"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Itens da Compra */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Itens da Compra</h2>
            <button
              type="button"
              onClick={addItem}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Adicionar Item
            </button>
          </div>

          {errors.itens && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.itens}
              </p>
            </div>
          )}

                  {formData.itens.length > 0 ? (
                    <div className="overflow-visible shadow-sm border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                      Produto *
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Qtd *
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Preço *
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subtotal
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {formData.itens.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-2 sm:px-4 py-3">
                        <div className="min-w-[200px]">
                          <SearchableSelect
                            options={products.map(product => ({
                              id: product.id,
                              nome: product.nome,
                              descricao: product.codigo
                            }))}
                            value={item.produtoId}
                            onChange={(value) => handleItemChange(index, 'produtoId', String(value))}
                            placeholder="Selecione um produto"
                            loading={searchingProducts}
                            error={errors[`item_${index}_produto`]}
                          />
                          {errors[`item_${index}_produto`] && (
                            <p className="mt-1 text-xs text-red-600">{errors[`item_${index}_produto`]}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3">
                        <div className="min-w-[80px]">
                          <input
                            type="text"
                            value={formatFromRaw(
                              quantidadeRawMap[index] ?? rawFromNumber(Number(item.quantidade || 0), QUANTIDADE_DECIMALS),
                              QUANTIDADE_DECIMALS
                            )}
                            onKeyDown={(e) => {
                              const currentRaw = quantidadeRawMap[index] ?? rawFromNumber(Number(item.quantidade || 0), QUANTIDADE_DECIMALS);
                              const { handled, nextRaw } = getNextRawOnKey(e.key, currentRaw);
                              if (handled) {
                                e.preventDefault();
                                setQuantidadeRawMap(prev => ({ ...prev, [index]: nextRaw }));
                                const parsed = parseRawToNumber(nextRaw, QUANTIDADE_DECIMALS);
                                handleItemChange(index, 'quantidade', parsed.toString());
                              }
                            }}
                            onChange={() => { /* controlado via onKeyDown */ }}
                            placeholder={formatFromRaw(rawFromNumber(0, QUANTIDADE_DECIMALS), QUANTIDADE_DECIMALS)}
                            inputMode="numeric"
                            className={`w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                              errors[`item_${index}_quantidade`] ? 'border-red-300' : 'border-gray-300'
                            }`}
                          />
                          {errors[`item_${index}_quantidade`] && (
                            <p className="mt-1 text-xs text-red-600">{errors[`item_${index}_quantidade`]}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3">
                        <div className="min-w-[100px]">
                          <input
                            type="text"
                            value={formatFromRaw(
                              precoRawMap[index] ?? rawFromNumber(Number(item.precoUnitario || 0), PRECO_DECIMALS),
                              PRECO_DECIMALS
                            )}
                            onKeyDown={(e) => {
                              const currentRaw = precoRawMap[index] ?? rawFromNumber(Number(item.precoUnitario || 0), PRECO_DECIMALS);
                              const { handled, nextRaw } = getNextRawOnKey(e.key, currentRaw);
                              if (handled) {
                                e.preventDefault();
                                setPrecoRawMap(prev => ({ ...prev, [index]: nextRaw }));
                                const parsed = parseRawToNumber(nextRaw, PRECO_DECIMALS);
                                handleItemChange(index, 'precoUnitario', parsed.toString());
                              }
                            }}
                            onChange={() => { /* controlado via onKeyDown */ }}
                            placeholder={formatFromRaw(rawFromNumber(0, PRECO_DECIMALS), PRECO_DECIMALS)}
                            inputMode="numeric"
                            className={`w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                              errors[`item_${index}_preco`] ? 'border-red-300' : 'border-gray-300'
                            }`}
                          />
                          {errors[`item_${index}_preco`] && (
                            <p className="mt-1 text-xs text-red-600">{errors[`item_${index}_preco`]}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.quantidade && item.precoUnitario ? (
                          <span className="text-green-600 text-xs sm:text-sm">
                            {formatCurrency(parseFloat(item.quantidade) * parseFloat(item.precoUnitario))}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs sm:text-sm">R$ 0,00</span>
                        )}
                      </td>
                      <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-sm">
                        <div className="flex flex-col sm:flex-row gap-1">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                            title="Remover item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {formData.itens.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <ShoppingCart className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>Nenhum item adicionado</p>
              <p className="text-sm">Clique em "Adicionar Item" para começar</p>
            </div>
          )}
        </div>

        {/* Resumo */}
        {formData.itens.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Resumo da Compra</h2>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total de itens:</span>
                <span className="font-medium">{formData.itens.length}</span>
              </div>
              <div className="flex justify-between items-center text-lg font-semibold">
                <span className="text-gray-900">Valor Total:</span>
                <span className="text-red-600">{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          </div>
        )}

        {/* Botões de Ação */}
        <div className="flex items-center justify-between pt-6">
          {/* Botões de Status (apenas para compras PENDENTES em modo de edição) */}
          {isEditing && formData.status === 'PENDENTE' && (
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={handleConfirmPurchase}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Confirmar Compra
              </button>
              <button
                type="button"
                onClick={handleCancelPurchase}
                disabled={loading}
                className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <X className="h-4 w-4" />
                )}
                Cancelar Compra
              </button>
            </div>
          )}
          
          {/* Botões de Navegação e Salvamento */}
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Voltar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {isEditing ? 'Atualizar' : 'Salvar'} Compra
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PurchaseInfoPage;